import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import MovieCard from '@/components/feature/MovieCard';
import { allContent, categoryNames, categories } from '@/mocks/content';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSubcategory, setActiveSubcategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('rating');

  const categoryName = slug ? (categoryNames[slug] || '未知分类') : '未知分类';
  const categoryItems = allContent.filter((item) => item.category === slug);

  const subcategories = Array.from(new Set(categoryItems.map((item) => item.subcategory)));

  let filteredItems = activeSubcategory === 'all'
    ? categoryItems
    : categoryItems.filter((item) => item.subcategory === activeSubcategory);

  if (sortBy === 'rating') {
    filteredItems = [...filteredItems].sort((a, b) => b.rating - a.rating);
  } else if (sortBy === 'year') {
    filteredItems = [...filteredItems].sort((a, b) => b.year - a.year);
  } else if (sortBy === 'newest') {
    filteredItems = [...filteredItems].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
  }

  const seoTitle = `${categoryName}全集在线观看 - 色影视`;
  const seoDesc = `${categoryName}全集在线观看，高清不缓冲流畅播放，最新${categoryName}大全一网打尽。色影视为您提供最全${categoryName}免费在线观看体验。`;

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="WebPage" data={{
        name: seoTitle,
        description: seoDesc,
        url: `https://www.seyingtv.com/category/${slug}`,
      }} />
      <SEOSchema type="CollectionPage" data={{
        name: `${categoryName} - 色影视`,
        description: seoDesc,
        url: `https://www.seyingtv.com/category/${slug}`,
      }} />
      <SEOSchema type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '色影视首页', item: 'https://www.seyingtv.com/' },
          { '@type': 'ListItem', position: 2, name: categoryName },
        ],
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
              {categoryName}
              <span className="text-base font-normal text-foreground-500 ml-3">共 {categoryItems.length} 部</span>
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              {categoryName}全集在线观看，高清不缓冲流畅播放，最新{categoryName}大全一网打尽
            </p>

            {/* Subcategory Tabs */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-2">分类：</span>
              <button
                onClick={() => setActiveSubcategory('all')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeSubcategory === 'all'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {subcategories.map((sub) => (
                <button
                  key={sub}
                  onClick={() => setActiveSubcategory(sub)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeSubcategory === sub
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {sub}
                </button>
              ))}
            </div>

            {/* Sort */}
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-foreground-500 mr-2">排序：</span>
              {[
                { value: 'rating', label: '评分最高' },
                { value: 'year', label: '最新发布' },
                { value: 'newest', label: '最新上线' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    sortBy === opt.value
                      ? 'bg-accent-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Grid */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredItems.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>

            {filteredItems.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}